import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import MobileShell from "@/components/MobileShell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import VideoPreview from "@/components/VideoPreview";
import ExerciseTimer from "@/components/ExerciseTimer";
import api, { formatApiError } from "@/lib/api";
import { activePrescriptionRows, dosageLabel, patientExercise } from "@/lib/prescriptionDelivery";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

const statusChoices = [
  ["not_attempted", "Not attempted"],
  ["completed", "Completed"],
  ["partial", "Partially completed"],
  ["skipped", "Skipped"],
];

function newKey() {
  return globalThis.crypto?.randomUUID?.() || `session-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export default function DailySession() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [legacyExercises, setLegacyExercises] = useState([]);
  const [session, setSession] = useState(null);
  const [latest, setLatest] = useState(null);
  const [history, setHistory] = useState([]);
  const [legacyCount, setLegacyCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");
  const [detailIndex, setDetailIndex] = useState(null);
  const [reload, setReload] = useState(0);
  const busyRef = useRef(false);
  const startKey = useRef(null);
  const submitKey = useRef(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError("");
    Promise.all([
      api.get("/plans/me/active/all"),
      api.get("/rehab-sessions/me/current"),
      api.get("/rehab-sessions/me/latest"),
      api.get("/rehab-sessions/me/history"),
      api.get("/sessions/me"),
      api.get("/dashboard/me"),
    ]).then(async ([planResult, currentResult, latestResult, historyResult, legacyResult, dashboardResult]) => {
      if (!mounted) return;
      if (!Array.isArray(planResult.data)) throw new Error("Unable to load your prescription.");
      setPlans(planResult.data);
      if (!planResult.data.length) {
        const condition = dashboardResult.data?.condition;
        if (condition?.slug) {
          const library = await api.get("/exercises", { params: { condition: condition.slug } });
          if (mounted) setLegacyExercises(Array.isArray(library.data) && library.data.length
            ? library.data : condition.exercises || []);
        } else setLegacyExercises([]);
      }
      setSession(currentResult.data?.id ? currentResult.data : null);
      setNotes(currentResult.data?.notes || "");
      setLatest(latestResult.data?.id ? latestResult.data : null);
      setHistory(historyResult.data || []);
      setLegacyCount((legacyResult.data || []).length);
    }).catch((err) => {
      if (mounted) setError(formatApiError(err.response?.data?.detail || err.message || "Unable to load sessions."));
    }).finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [reload]);

  const run = async (work) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      await work();
    } catch (err) {
      const message = formatApiError(err.response?.data?.detail) || err.message || "Unable to save.";
      toast.error(message);
      if (err.response?.status === 409) setReload((value) => value + 1);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const start = () => run(async () => {
    startKey.current ||= newKey();
    const { data } = await api.post("/rehab-sessions/me/start", { idempotency_key: startKey.current });
    setSession(data);
    setNotes(data.notes || "");
    submitKey.current = null;
    toast.success("Session started");
  });

  const setLineStatus = (line, status) => run(async () => {
    const { data } = await api.put(`/rehab-sessions/me/${session.id}/lines/${line.id}`, {
      status, expected_version: session.version,
    });
    setSession(data);
  });

  const saveDraft = () => run(async () => {
    const { data } = await api.put(`/rehab-sessions/me/${session.id}/draft`, {
      notes, expected_version: session.version,
    });
    setSession(data);
    toast.success("Notes saved");
  });

  const submit = () => run(async () => {
    let current = session;
    if (notes !== session.notes) {
      const saved = await api.put(`/rehab-sessions/me/${session.id}/draft`, {
        notes, expected_version: session.version,
      });
      current = saved.data;
      setSession(current);
    }
    submitKey.current ||= newKey();
    try {
      const { data } = await api.post(`/rehab-sessions/me/${current.id}/submit`, {
        idempotency_key: submitKey.current, expected_version: current.version,
      });
      setLatest(data);
      setHistory((rows) => [data, ...rows.filter((row) => row.id !== data.id)]);
      setSession(null);
      setNotes("");
      startKey.current = null;
      submitKey.current = null;
      toast.success("Session saved");
    } catch (err) {
      // A lost response may follow a successful server commit. Read the authoritative receipt.
      const refreshed = await api.get(`/rehab-sessions/me/${current.id}`).catch(() => null);
      if (refreshed?.data?.status === "submitted") {
        setLatest(refreshed.data);
        setHistory((rows) => [refreshed.data, ...rows.filter((row) => row.id !== refreshed.data.id)]);
        setSession(null);
        setNotes("");
        startKey.current = null;
        submitKey.current = null;
        toast.success("Session saved");
      } else throw err;
    }
  });

  const abandon = () => run(async () => {
    await api.post(`/rehab-sessions/me/${session.id}/abandon`, { expected_version: session.version });
    setSession(null);
    setNotes("");
    startKey.current = null;
    submitKey.current = null;
    setReload((value) => value + 1);
  });

  if (loading) return <MobileShell><div className="p-6 text-slate-500" role="status">Loading your prescription…</div></MobileShell>;
  if (error) return <MobileShell><div className="p-6" role="alert">{error}<Button className="ml-3" onClick={() => setReload((value) => value + 1)}>Try again</Button></div></MobileShell>;

  const rows = session ? session.lines.map((line) => ({ ...line.prescription, session_line_id: line.id, session_status: line.status }))
    : plans.length ? activePrescriptionRows(plans) : legacyExercises;
  const opened = detailIndex === null ? null : rows[detailIndex];
  const details = opened ? patientExercise(opened) : null;
  const recorded = session?.lines.filter((line) => line.status !== "not_attempted").length || 0;

  return <MobileShell>
    <header className="px-6 pt-8 pb-3">
      <button onClick={() => navigate(-1)} className="mb-3 text-slate-500" data-testid="back-btn" aria-label="Back"><ArrowLeft size={20} /></button>
      <h1 className="text-2xl font-bold font-display text-slate-900">Rehabilitation session</h1>
      <p className="text-sm text-slate-600 mt-1">{rows.length} prescribed exercise{rows.length === 1 ? "" : "s"}</p>
      {session && <p className="text-sm text-teal-700 mt-1" data-testid="session-date">Started {session.treatment_date} (Sydney time) · In progress</p>}
      {!session && !plans.length && rows.length > 0 && <p className="mt-2 text-sm text-amber-800">These earlier pathway exercises are for reference only. Ask your therapist for an active prescription to track sessions.</p>}
    </header>

    {session?.prescription_changed && <section className="mx-6 rounded-xl border border-amber-300 bg-amber-50 p-4" role="alert">
      <p>Your prescription changed after this session started. Your draft remains available for review. End this draft to begin a session with the current prescription.</p>
      <Button className="mt-3" variant="outline" disabled={busy} onClick={abandon}>End draft and review current plan</Button>
    </section>}

    <section className="px-6 mt-4">
      <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500">Exercises</h2>
      {!rows.length && <div className="mt-3 rounded-2xl border bg-white p-4 text-sm" role="status">No exercises have been assigned yet. Please contact your therapist if you expected a prescription.</div>}
      <ul className="mt-2 space-y-2" data-testid="exercise-list">
        {rows.map((row, index) => {
          const exercise = patientExercise(row);
          return <li key={row.session_line_id || row.id || index} className="rounded-2xl border border-slate-100 bg-white p-3">
            <button type="button" onClick={() => setDetailIndex(index)} data-testid={`ex-detail-${index}`} className="w-full text-left">
              <div className="font-semibold text-slate-900">{exercise.title}</div>
              {row.source_plan_title && <div className="text-xs text-slate-500">{row.source_plan_title}</div>}
              <div className="text-xs text-slate-600">{dosageLabel(exercise)}</div>
              {exercise.instructions && <div className="text-xs text-slate-600 mt-1 line-clamp-2">{exercise.instructions}</div>}
            </button>
            {session && <fieldset className="mt-3 flex flex-wrap gap-2" disabled={busy || session.prescription_changed}>
              <legend className="sr-only">Status for {exercise.title}</legend>
              {statusChoices.map(([value, label]) => <button
                type="button" key={value} data-testid={`line-${index}-${value}`}
                aria-pressed={row.session_status === value}
                onClick={() => setLineStatus(session.lines[index], value)}
                className={`rounded-full border px-3 py-1 text-xs ${row.session_status === value ? "border-teal-700 bg-teal-50 text-teal-800" : "border-slate-200 text-slate-600"}`}
              >{label}</button>)}
            </fieldset>}
            <ExerciseTimer exercise={row} />
          </li>;
        })}
      </ul>
    </section>

    {session && <section className="px-6 mt-4">
      <label htmlFor="session-notes" className="text-sm font-semibold">Session notes (optional)</label>
      <Textarea id="session-notes" data-testid="session-notes" maxLength={4000} value={notes} onChange={(event) => setNotes(event.target.value)} className="mt-2 bg-white" />
      <Button variant="outline" className="mt-2" disabled={busy || session.prescription_changed} onClick={saveDraft}>Save notes</Button>
    </section>}

    <section className="px-6 mt-5 mb-8">
      {!session && plans.length > 0 && rows.length > 0 && <Button onClick={start} disabled={busy} data-testid="start-session-btn" className="w-full rounded-full">Start a session</Button>}
      {session && <Button onClick={submit} disabled={busy || session.prescription_changed || recorded === 0} data-testid="complete-session-btn" className="w-full rounded-full">
        {busy ? "Saving…" : `Finish session (${recorded}/${rows.length} recorded)`}
      </Button>}
      {session && <p className="mt-2 text-xs text-slate-600">Your exercise choices are saved as you go. Return later or use another device to resume. Notes are saved with the Save notes button or when you finish.</p>}
      {latest && <div className="mt-4 rounded-xl border bg-white p-3 text-sm" data-testid="latest-session">Last submitted session: {latest.treatment_date} · {latest.summary?.completed || 0} completed, {latest.summary?.partial || 0} partial, {latest.summary?.skipped || 0} skipped.</div>}
      {history.length > 0 && <div className="mt-4 text-sm"><h2 className="font-semibold">Session history</h2><ul className="mt-1 space-y-1" data-testid="session-history">{history.map((item) => <li key={item.id}>{item.treatment_date} · {item.status} · {item.summary?.performed || 0} performed</li>)}</ul></div>}
      {legacyCount > 0 && <p className="mt-3 text-xs text-slate-500">{legacyCount} earlier session record{legacyCount === 1 ? "" : "s"} retained; treatment dates and exercise-level status may be unverified.</p>}
    </section>

    <Dialog open={detailIndex !== null} onOpenChange={(open) => !open && setDetailIndex(null)}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl">
        <DialogHeader><DialogTitle>{details?.title}</DialogTitle></DialogHeader>
        {details && <div className="space-y-4">
          <VideoPreview url={details.video_url} title={details.title} />
          <Detail label="Instructions" value={details.instructions || "Ask your physiotherapist for instructions."} />
          <Detail label="Technique cues" value={details.key_cues} />
          <Detail label="Patient notes" value={details.patient_notes} />
          <div className="grid gap-3 sm:grid-cols-2">
            <Detail label="Sets" value={details.sets} /><Detail label="Reps" value={details.reps} />
            <Detail label="Hold time" value={details.hold_time} /><Detail label="Frequency" value={details.frequency} />
          </div>
          <Detail label="Equipment" value={details.equipment} /><Detail label="Safety notes" value={details.safety_notes} />
          <div className="flex justify-between border-t pt-3">
            <Button variant="outline" disabled={detailIndex === 0} onClick={() => setDetailIndex((value) => value - 1)}>Previous</Button>
            <span className="text-sm">{detailIndex + 1} of {rows.length}</span>
            <Button variant="outline" disabled={detailIndex === rows.length - 1} onClick={() => setDetailIndex((value) => value + 1)}>Next</Button>
          </div>
        </div>}
      </DialogContent>
    </Dialog>
  </MobileShell>;
}

function Detail({ label, value }) {
  if (!value) return null;
  return <div><div className="text-xs font-bold uppercase text-slate-500">{label}</div><div className="text-sm text-slate-700">{value}</div></div>;
}
